export type CookieDetail = {
  name: string;
  provider: string;
  purpose: string;
  duration: string;
};

export type ConsentTexts = {
  title: string;
  intro: string;
  privacyLink: string;
  acceptAll: string;
  necessaryOnly: string;
  settings: string;
  saveSelection: string;
  necessaryTitle: string;
  necessaryText: string;
  alwaysOn: string;
  mapsTitle: string;
  mapsText: string;
  showDetails: string;
  hideDetails: string;
  colName: string;
  colProvider: string;
  colPurpose: string;
  colDuration: string;
  necessaryCookies: CookieDetail[];
  mapsCookies: CookieDetail[];
  footerLink: string;
  mapPlaceholder: string;
  mapLoad: string;
  mapConfirmTitle: string;
  mapConfirmText: string;
  mapConfirmAccept: string;
  mapConfirmCancel: string;
  mapConfirmSettings: string;
};

const en: ConsentTexts = {
  title: "Before your journey starts",
  intro:
    "We use cookies that our website needs to work (payment, login, language). With your consent we also show maps from Google Maps; Google then receives your IP address and may set cookies. You can change your choice at any time via “Cookie settings” at the bottom of the page.",
  privacyLink: "Privacy policy",
  acceptAll: "Accept all",
  necessaryOnly: "Necessary only",
  settings: "Settings",
  saveSelection: "Save selection",
  necessaryTitle: "Necessary",
  necessaryText:
    "Payment via Stripe (fraud prevention), your login, your language and your cookie choice. These keep the website working.",
  alwaysOn: "Always on",
  mapsTitle: "External media: Google Maps",
  mapsText:
    "Shows tour routes and places on a map and suggests places in forms. Provider: Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Ireland. Google receives your IP address and the place shown; data may be transferred to the USA (Google is certified under the EU–US Data Privacy Framework).",
  showDetails: "Show details",
  hideDetails: "Hide details",
  colName: "Name",
  colProvider: "Provider",
  colPurpose: "Purpose",
  colDuration: "Storage period",
  necessaryCookies: [
    {
      name: "swt_consent",
      provider: "socialtravel.world",
      purpose: "Stores your cookie choice",
      duration: "12 months",
    },
    {
      name: "swt-token (local storage)",
      provider: "socialtravel.world",
      purpose: "Keeps you logged in",
      duration: "Until you log out",
    },
    {
      name: "language (local storage)",
      provider: "socialtravel.world",
      purpose: "Remembers your language",
      duration: "Until you delete it",
    },
    {
      name: "__stripe_mid",
      provider: "Stripe",
      purpose: "Fraud prevention for payments",
      duration: "1 year",
    },
    {
      name: "__stripe_sid",
      provider: "Stripe",
      purpose: "Fraud prevention for payments",
      duration: "30 minutes",
    },
    {
      name: "m",
      provider: "m.stripe.com",
      purpose: "Fraud prevention for payments",
      duration: "Up to 2 years",
    },
    {
      name: "_ab, _mf, id, 1 (session storage)",
      provider: "m.stripe.network",
      purpose: "Secure payment window and fraud prevention",
      duration: "Until you close the browser",
    },
  ],
  mapsCookies: [
    {
      name: "NID and other Google cookies",
      provider: "Google (google.com)",
      purpose:
        "Loading the map, remembering map settings, security; Google may also use them for its own purposes",
      duration: "Up to 13 months",
    },
  ],
  footerLink: "Cookie settings",
  mapPlaceholder:
    "This map is provided by Google Maps. When you load it, Google receives your IP address and may set cookies.",
  mapLoad: "Load map (Google Maps)",
  mapConfirmTitle: "Allow Google Maps?",
  mapConfirmText:
    "To show the map, you consent to the use of Google Maps. Google receives your IP address and the place shown, may set cookies and may transfer data to the USA. Your consent applies to all maps and place suggestions on this website. You can withdraw it at any time via “Cookie settings” at the bottom of the page.",
  mapConfirmAccept: "Allow and load map",
  mapConfirmCancel: "Cancel",
  mapConfirmSettings: "Cookie settings",
};

const de: ConsentTexts = {
  title: "Bevor die Reise losgeht",
  intro:
    "Wir nutzen Cookies, die unsere Website zum Funktionieren braucht (Zahlung, Login, Sprache). Mit deiner Einwilligung zeigen wir außerdem Karten von Google Maps; Google erhält dann deine IP-Adresse und kann Cookies setzen. Deine Auswahl kannst du jederzeit unten auf der Seite unter „Cookie-Einstellungen“ ändern.",
  privacyLink: "Datenschutzerklärung",
  acceptAll: "Alle akzeptieren",
  necessaryOnly: "Nur notwendige",
  settings: "Einstellungen",
  saveSelection: "Auswahl speichern",
  necessaryTitle: "Notwendig",
  necessaryText:
    "Zahlung über Stripe (Betrugsschutz), dein Login, deine Sprache und deine Cookie-Auswahl. Damit funktioniert die Website.",
  alwaysOn: "Immer aktiv",
  mapsTitle: "Externe Medien: Google Maps",
  mapsText:
    "Zeigt Reiserouten und Orte auf einer Karte und schlägt in Formularen Orte vor. Anbieter: Google Ireland Limited, Gordon House, Barrow Street, Dublin 4, Irland. Google erhält deine IP-Adresse und den angezeigten Ort; Daten können in die USA übertragen werden (Google ist unter dem EU-US Data Privacy Framework zertifiziert).",
  showDetails: "Details anzeigen",
  hideDetails: "Details ausblenden",
  colName: "Name",
  colProvider: "Anbieter",
  colPurpose: "Zweck",
  colDuration: "Speicherdauer",
  necessaryCookies: [
    {
      name: "swt_consent",
      provider: "socialtravel.world",
      purpose: "Speichert deine Cookie-Auswahl",
      duration: "12 Monate",
    },
    {
      name: "swt-token (lokaler Speicher)",
      provider: "socialtravel.world",
      purpose: "Hält dich eingeloggt",
      duration: "Bis zum Logout",
    },
    {
      name: "language (lokaler Speicher)",
      provider: "socialtravel.world",
      purpose: "Merkt sich deine Sprache",
      duration: "Bis du ihn löschst",
    },
    {
      name: "__stripe_mid",
      provider: "Stripe",
      purpose: "Betrugsschutz bei Zahlungen",
      duration: "1 Jahr",
    },
    {
      name: "__stripe_sid",
      provider: "Stripe",
      purpose: "Betrugsschutz bei Zahlungen",
      duration: "30 Minuten",
    },
    {
      name: "m",
      provider: "m.stripe.com",
      purpose: "Betrugsschutz bei Zahlungen",
      duration: "Bis zu 2 Jahre",
    },
    {
      name: "_ab, _mf, id, 1 (Sitzungsspeicher)",
      provider: "m.stripe.network",
      purpose: "Sicheres Zahlungsfenster und Betrugsschutz",
      duration: "Bis du den Browser schließt",
    },
  ],
  mapsCookies: [
    {
      name: "NID und weitere Google-Cookies",
      provider: "Google (google.com)",
      purpose:
        "Laden der Karte, Kartenvoreinstellungen, Sicherheit; Google kann sie auch für eigene Zwecke nutzen",
      duration: "Bis zu 13 Monate",
    },
  ],
  footerLink: "Cookie-Einstellungen",
  mapPlaceholder:
    "Diese Karte stammt von Google Maps. Wenn du sie lädst, erhält Google deine IP-Adresse und kann Cookies setzen.",
  mapLoad: "Karte laden (Google Maps)",
  mapConfirmTitle: "Google Maps erlauben?",
  mapConfirmText:
    "Um die Karte anzuzeigen, willigst du in die Nutzung von Google Maps ein. Google erhält deine IP-Adresse und den angezeigten Ort, kann Cookies setzen und Daten in die USA übertragen. Deine Einwilligung gilt für alle Karten und Ortsvorschläge auf dieser Website. Du kannst sie jederzeit unten auf der Seite unter „Cookie-Einstellungen“ widerrufen.",
  mapConfirmAccept: "Erlauben und Karte laden",
  mapConfirmCancel: "Abbrechen",
  mapConfirmSettings: "Cookie-Einstellungen",
};

const es: ConsentTexts = {
  title: "Antes de que empiece el viaje",
  intro:
    "Usamos cookies que nuestra web necesita para funcionar (pago, inicio de sesión, idioma). Con tu consentimiento también mostramos mapas de Google Maps; Google recibe entonces tu dirección IP y puede instalar cookies. Puedes cambiar tu elección en cualquier momento en «Configuración de cookies», al final de la página.",
  privacyLink: "Política de privacidad",
  acceptAll: "Aceptar todo",
  necessaryOnly: "Solo necesarias",
  settings: "Configuración",
  saveSelection: "Guardar selección",
  necessaryTitle: "Necesarias",
  necessaryText:
    "Pago con Stripe (prevención del fraude), tu inicio de sesión, tu idioma y tu elección de cookies. Hacen que la web funcione.",
  alwaysOn: "Siempre activas",
  mapsTitle: "Contenido externo: Google Maps",
  mapsText:
    "Muestra rutas y lugares de los viajes en un mapa y sugiere lugares en los formularios. Proveedor: Google Ireland Limited, Gordon House, Barrow Street, Dublín 4, Irlanda. Google recibe tu dirección IP y el lugar mostrado; los datos pueden transferirse a EE. UU. (Google está certificado en el marco de privacidad de datos UE-EE. UU.).",
  showDetails: "Ver detalles",
  hideDetails: "Ocultar detalles",
  colName: "Nombre",
  colProvider: "Proveedor",
  colPurpose: "Finalidad",
  colDuration: "Duración",
  necessaryCookies: [
    {
      name: "swt_consent",
      provider: "socialtravel.world",
      purpose: "Guarda tu elección de cookies",
      duration: "12 meses",
    },
    {
      name: "swt-token (almacenamiento local)",
      provider: "socialtravel.world",
      purpose: "Mantiene tu sesión iniciada",
      duration: "Hasta que cierres sesión",
    },
    {
      name: "language (almacenamiento local)",
      provider: "socialtravel.world",
      purpose: "Recuerda tu idioma",
      duration: "Hasta que lo borres",
    },
    {
      name: "__stripe_mid",
      provider: "Stripe",
      purpose: "Prevención del fraude en los pagos",
      duration: "1 año",
    },
    {
      name: "__stripe_sid",
      provider: "Stripe",
      purpose: "Prevención del fraude en los pagos",
      duration: "30 minutos",
    },
    {
      name: "m",
      provider: "m.stripe.com",
      purpose: "Prevención del fraude en los pagos",
      duration: "Hasta 2 años",
    },
    {
      name: "_ab, _mf, id, 1 (almacenamiento de sesión)",
      provider: "m.stripe.network",
      purpose: "Ventana de pago segura y prevención del fraude",
      duration: "Hasta que cierres el navegador",
    },
  ],
  mapsCookies: [
    {
      name: "NID y otras cookies de Google",
      provider: "Google (google.com)",
      purpose:
        "Cargar el mapa, preferencias del mapa, seguridad; Google también puede usarlas para sus propios fines",
      duration: "Hasta 13 meses",
    },
  ],
  footerLink: "Configuración de cookies",
  mapPlaceholder:
    "Este mapa lo proporciona Google Maps. Al cargarlo, Google recibe tu dirección IP y puede instalar cookies.",
  mapLoad: "Cargar mapa (Google Maps)",
  mapConfirmTitle: "¿Permitir Google Maps?",
  mapConfirmText:
    "Para mostrar el mapa, aceptas el uso de Google Maps. Google recibe tu dirección IP y el lugar mostrado, puede instalar cookies y transferir datos a EE. UU. Tu consentimiento vale para todos los mapas y sugerencias de lugares de esta web. Puedes retirarlo en cualquier momento en «Configuración de cookies», al final de la página.",
  mapConfirmAccept: "Permitir y cargar mapa",
  mapConfirmCancel: "Cancelar",
  mapConfirmSettings: "Configuración de cookies",
};

// Site languages: en, de, es (language switch in SWTNavbar). Anything else
// falls back to English.
export const consentTexts = (language?: string | null): ConsentTexts => {
  const code = language?.toLowerCase() ?? "";
  if (code.startsWith("de")) return de;
  if (code.startsWith("es")) return es;
  return en;
};
